import React, { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { ListingPerformance } from "@/components/ListingPerformance";
import SocialCampaigns from "@/components/SocialCampaigns";
import { NeighborhoodDataPreview } from "@/components/NeighborhoodDataPreview";
import { FloorplanTourPreview } from "@/components/FloorplanTourPreview";
import { AgentInsightsSection } from "@/components/AgentInsightsSection";
import demoPropertyImage from "@/assets/demo-property.jpg";
import { useDefaultLogo } from "@/hooks/useDefaultLogo";
import { format } from "date-fns";
import { Home, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatMarkdownToSafeHtml } from "@/lib/sanitize";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { ZillowIcon } from "@/components/icons/ZillowIcon";

const DEMO_NORTH_ID = "aaaaaaaa-0000-0000-0000-000000000001";

// Anonymous demo data
const DEMO_DATA = {
  address: "1234 Example Street",
  city: "Example City",
  state: "CA",
  zip: "90210",
  price: 1250000,
  beds: 4,
  baths: 3,
  sqft: 2850,
  listing_agent: "Sarah Johnson",
  executive_summary: "This stunning contemporary home offers the perfect blend of luxury and comfort. With its open-concept design, high-end finishes, and resort-style outdoor living space, this property represents exceptional value in today's market. **Strategic marketing** across multiple channels generated significant buyer interest, resulting in **over 15,000 impressions** in the first two weeks. The property's unique features and prime location have attracted **serious buyers** from both local and international markets."
};

// Hardcoded demo showings data
const DEMO_SHOWINGS = [
  {
    id: 'demo-showing-1',
    title: 'Follow-Up Showing with Contractor',
    feedback: 'Repeat showing - second visit. Bringing contractor to discuss minor customizations. Very serious and motivated buyers. Pre-qualified and ready to proceed.',
    scheduled_date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    event_type: 'showing',
    attendees_count: 2,
  },
  {
    id: 'demo-showing-2',
    title: 'Private Showing - Corporate Relocation',
    feedback: 'Relocating for work. Timeline is flexible but very interested. Want to make decision before month end. Property exceeds their expectations.',
    scheduled_date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    event_type: 'showing',
    attendees_count: 2,
  },
  {
    id: 'demo-showing-3',
    title: 'Open House',
    feedback: 'Excellent turnout with 28 visitors. Multiple serious buyers expressed interest. Three requested private showings.',
    scheduled_date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    event_type: 'open_house',
    attendees_count: 28,
  },
  {
    id: 'demo-showing-4',
    title: 'Private Showing',
    feedback: 'First-time buyers very impressed with the property. Requested information on financing options.',
    scheduled_date: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    event_type: 'showing',
    attendees_count: 2,
  },
  {
    id: 'demo-showing-5',
    title: 'Private Showing',
    feedback: 'Investor buyer interested in the property. Discussed potential rental income and ROI.',
    scheduled_date: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000).toISOString(),
    event_type: 'showing',
    attendees_count: 1,
  },
];

// Hardcoded demo comparables data
const DEMO_COMPS = [
  {
    id: 'demo-comp-1',
    address: '9001 Beverly Blvd',
    city: 'Beverly Hills',
    state: 'CA',
    zip: '90210',
    price: 1350000,
    beds: 4,
    baths: 3.5,
    sqft: 2650,
    status: 'For Sale',
    image_url: '/demo-comp-property.jpg',
    zillow_url: 'https://www.zillow.com/',
  },
  {
    id: 'demo-comp-2',
    address: '9125 Sunset Boulevard',
    city: 'Beverly Hills',
    state: 'CA',
    zip: '90210',
    price: 1650000,
    beds: 5,
    baths: 4,
    sqft: 3200,
    status: 'For Sale',
    image_url: '/demo-comp-property.jpg',
    zillow_url: 'https://www.zillow.com/',
  },
  {
    id: 'demo-comp-3',
    address: '1234 Rodeo Drive',
    city: 'Beverly Hills',
    state: 'CA',
    zip: '90210',
    price: 1150000,
    beds: 3,
    baths: 2.5,
    sqft: 2200,
    status: 'Sold',
    image_url: '/demo-comp-property.jpg',
    zillow_url: 'https://www.zillow.com/',
  },
  {
    id: 'demo-comp-4',
    address: '4567 Wilshire Boulevard',
    city: 'Beverly Hills',
    state: 'CA',
    zip: '90210',
    price: 1420000,
    beds: 4,
    baths: 3,
    sqft: 2800,
    status: 'For Sale',
    image_url: '/demo-comp-property.jpg',
    zillow_url: 'https://www.zillow.com/',
  },
  {
    id: 'demo-comp-5',
    address: '7890 Santa Monica Blvd',
    city: 'Beverly Hills',
    state: 'CA',
    zip: '90210',
    price: 1280000,
    beds: 3,
    baths: 2.5,
    sqft: 2400,
    status: 'Pending',
    image_url: '/demo-comp-property.jpg',
    zillow_url: 'https://www.zillow.com/',
  },
];

export default function Demo() {
  const { defaultLogoUrl } = useDefaultLogo();
  const [showAllShowings, setShowAllShowings] = useState(false);
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [mapboxToken, setMapboxToken] = useState("");
  const [webglError, setWebglError] = useState(false);
  
  // Fetch Mapbox token for demo
  useEffect(() => {
    const fetchToken = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('get-mapbox-token', { body: { demo: true } });
        if (error) throw error;
        if (data?.token) {
          setMapboxToken(data.token);
        }
      } catch (error) {
        console.error('Error fetching Mapbox token:', error);
      }
    };
    fetchToken();
  }, []);
  
  // Initialize map with markers
  useEffect(() => {
    if (!mapContainer.current || !mapboxToken || DEMO_COMPS.length === 0 || webglError) return;
    
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('webgl2');
    if (!gl) {
      setWebglError(true);
      return;
    }
    
    mapboxgl.accessToken = mapboxToken;
    const bounds = new mapboxgl.LngLatBounds();
    
    try {
      // Center map on Beverly Hills 90210
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [-118.4065, 34.0901], // Beverly Hills center
        zoom: 13,
        failIfMajorPerformanceCaveat: false,
      });
      
      map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
      
      map.current.on('error', (e) => {
        if (e.error?.message?.includes('WebGL')) {
          setWebglError(true);
        }
      });
      
      map.current.on('load', async () => {
        // Add subject property marker (home icon)
        const subjectLng = -118.4065 + (Math.random() - 0.5) * 0.02;
        const subjectLat = 34.0901 + (Math.random() - 0.5) * 0.02;
        bounds.extend([subjectLng, subjectLat]);
        
        const homeEl = document.createElement('div');
        homeEl.style.width = '32px';
        homeEl.style.height = '32px';
        homeEl.style.backgroundColor = 'white';
        homeEl.style.borderRadius = '50%';
        homeEl.style.display = 'flex';
        homeEl.style.alignItems = 'center';
        homeEl.style.justifyContent = 'center';
        homeEl.style.cursor = 'pointer';
        homeEl.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)';
        homeEl.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0c0f24" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
            <polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
        `;
        
        new mapboxgl.Marker(homeEl)
          .setLngLat([subjectLng, subjectLat])
          .setPopup(new mapboxgl.Popup({ offset: 15 }).setHTML(
            `<div style="padding: 8px;">
              <p style="font-weight: 600; margin-bottom: 4px;">${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(DEMO_DATA.price)}</p>
              <p style="font-size: 12px; margin-bottom: 2px;">${DEMO_DATA.address}</p>
              <p style="font-size: 11px; color: #666;">${DEMO_DATA.beds} bed • ${DEMO_DATA.baths} bath • ${DEMO_DATA.sqft.toLocaleString()} sqft</p>
            </div>`
          ))
          .addTo(map.current);
        
        // Add comparable property markers
        for (let i = 0; i < DEMO_COMPS.length; i++) {
          const comp = DEMO_COMPS[i];
          const lng = -118.4065 + (Math.random() - 0.5) * 0.02;
          const lat = 34.0901 + (Math.random() - 0.5) * 0.02;
          bounds.extend([lng, lat]);
          
          const el = document.createElement('div');
          el.style.width = '20px';
          el.style.height = '20px';
          el.style.backgroundColor = '#1d4ed8';
          el.style.borderRadius = '50%';
          el.style.cursor = 'pointer';
          el.style.border = '2px solid white';
          el.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
          
          const formatPrice = (price: number | null) => {
            if (!price) return '-';
            return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(price);
          };
          
          new mapboxgl.Marker(el)
            .setLngLat([lng, lat])
            .setPopup(new mapboxgl.Popup({ offset: 15 }).setHTML(
              `<div style="padding: 8px;">
                <p style="font-weight: 600; margin-bottom: 4px;">${formatPrice(comp.price)}</p>
                <p style="font-size: 12px; margin-bottom: 2px;">${comp.address}</p>
                <p style="font-size: 11px; color: #666;">${comp.beds} bed • ${comp.baths} bath • ${comp.sqft?.toLocaleString()} sqft</p>
              </div>`
            ))
            .addTo(map.current);
        }
        
        if (!bounds.isEmpty()) {
          map.current?.fitBounds(bounds, { padding: 50, maxZoom: 14 });
        }
      });
    } catch (error) {
      console.error('Failed to initialize map:', error);
      setWebglError(true);
      return;
    }
    
    return () => {
      map.current?.remove();
    };
  }, [mapboxToken, webglError]);
  
  // Fetch demo data in background (non-blocking) since we use static data for everything except logo
  const { data: demoData } = useQuery({
    queryKey: ['demo-report', DEMO_NORTH_ID],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('demo_reports')
          .select('*')
          .eq('id', DEMO_NORTH_ID)
          .maybeSingle(); // Use maybeSingle to avoid throwing if no row found

        if (error) {
          console.warn('[Demo] Failed to fetch demo report data:', error);
          return null; // Return null instead of throwing
        }
        return data;
      } catch (err) {
        console.error('[Demo] Exception in queryFn:', err);
        return null;
      }
    },
    retry: 1, // Only retry once
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  // Use anonymous demo data instead of database data
  // Render immediately with static data - don't wait for query since it's non-critical
  const fullAddress = `${DEMO_DATA.address}, ${DEMO_DATA.city}, ${DEMO_DATA.state} ${DEMO_DATA.zip}`;
  const agentName = DEMO_DATA.listing_agent;
  const logoUrl = demoData?.custom_logo_url || defaultLogoUrl;
  const agentHeadshot = null; // No headshot for anonymized demo

  return (
    <div className="min-h-screen bg-background">
      <div className="flex items-start justify-center px-4 py-8">
        <Card className="w-full max-w-5xl shadow-lg">
          <CardContent className="p-0">
            {/* Logo Header */}
            <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
              {logoUrl ? (
                <img src={logoUrl} alt="Logo" className="h-12 md:h-16" />
              ) : (
                <div className="h-12 md:h-16 w-32 md:w-40 bg-muted rounded flex items-center justify-center text-xs md:text-sm text-muted-foreground">
                  Logo
                </div>
              )}
              
              <div className="flex items-center gap-2 md:gap-3">
                <div className="text-right md:text-left md:order-first">
                  <p className="text-xs md:text-xs text-muted-foreground uppercase tracking-wider">Listed By</p>
                  <p className="font-semibold text-sm md:text-base text-foreground">{agentName}</p>
                </div>
                {agentHeadshot ? (
                  <img 
                    src={agentHeadshot} 
                    alt={agentName}
                    className="h-10 w-10 md:h-12 md:w-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold text-xs md:text-base">
                    {agentName.split(' ').map(n => n[0]).join('').toUpperCase()}
                  </div>
                )}
              </div>
            </div>

            {/* Property Image */}
            <div className="relative">
              <img 
                src={demoPropertyImage} 
                alt={fullAddress}
                className="w-full h-[400px] object-cover"
              />
            </div>

            {/* Content */}
            <div className="p-8">
              {/* Property Header */}
              <div className="mb-8">
                <div className="flex items-start justify-between mb-3">
                  <h1 className="text-xl md:text-3xl font-bold text-foreground">
                    {fullAddress}
                  </h1>
                  <p className="text-xl md:text-3xl font-bold text-foreground ml-8 whitespace-nowrap">
                    ${DEMO_DATA.price.toLocaleString()}
                  </p>
                </div>
                
                {/* Property Stats in single line */}
                <div className="flex items-center gap-3 text-base text-muted-foreground">
                  <span>{DEMO_DATA.beds} beds</span>
                  <span>•</span>
                  <span>{DEMO_DATA.baths} baths</span>
                  <span>•</span>
                  <span>{DEMO_DATA.sqft.toLocaleString()} sqft</span>
                </div>
              </div>

              {/* The Big Picture */}
              <div className="mb-12 p-8 rounded-xl bg-gray-50 border border-gray-200 shadow-sm">
                <h3 className="text-sm font-semibold tracking-wider text-gray-900 mb-4">
                  THE BIG PICTURE
                </h3>
                <div 
                  className="text-base text-gray-700 leading-relaxed prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ 
                    __html: DEMO_DATA.executive_summary.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                  }}
                />
              </div>

              {/* Listing Performance */}
              <div className="mb-12">
                <ListingPerformance
                  northId={DEMO_NORTH_ID}
                  zillowViews={3847}
                  primaryColor="#0c0f24"
                  secondaryColor="#1b489b"
                />
              </div>

              {/* Social Campaigns */}
              <div className="mb-12 p-8 rounded-xl bg-gray-50 border border-gray-200 shadow-sm">
                <div className="mb-4 md:mb-6">
                  <h3 className="text-xs md:text-sm font-semibold tracking-wider text-gray-900 mb-4 md:mb-6">
                    THE MARKETING MAGIC
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-3 md:gap-6">
                    {/* Instagram Post */}
                    <div className="bg-background rounded-lg overflow-hidden border border-border shadow-sm">
                      <div className="p-2 md:p-3 flex items-center gap-2 border-b border-border">
                        <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-muted flex items-center justify-center">
                          <svg className="w-3 h-3 md:w-4 md:h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                            <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                            <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                          </svg>
                        </div>
                        <div className="flex-1">
                          <p className="text-xs md:text-sm font-medium text-foreground">@homesbyexample</p>
                        </div>
                      </div>
                      <div className="relative aspect-square bg-muted">
                        <img 
                          src={demoPropertyImage} 
                          alt="Instagram post"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute bottom-2 right-2 md:bottom-3 md:right-3">
                          <div className="bg-black/60 text-white text-[10px] md:text-xs px-1.5 md:px-2 py-0.5 md:py-1 rounded">
                            Carousel
                          </div>
                        </div>
                      </div>
                      <div className="p-2 md:p-3">
                        <p className="text-xs md:text-sm text-muted-foreground line-clamp-3">
                          🏡 Just listed! This stunning home at {DEMO_DATA.address} features {DEMO_DATA.beds} beds, {DEMO_DATA.baths} baths. Perfect for families!
                        </p>
                        <div className="flex items-center gap-2 md:gap-4 mt-2 md:mt-3 text-[10px] md:text-xs text-muted-foreground flex-wrap">
                          <span>2,847 likes</span>
                          <span>156 comments</span>
                          <span className="hidden md:inline">48,392 impressions</span>
                        </div>
                      </div>
                    </div>

                    {/* Facebook Post */}
                    <div className="bg-background rounded-lg overflow-hidden border border-border shadow-sm">
                      <div className="p-2 md:p-3 flex items-center gap-2 border-b border-border">
                        <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-muted flex items-center justify-center">
                          <svg className="w-3 h-3 md:w-4 md:h-4" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                          </svg>
                        </div>
                        <div className="flex-1">
                          <p className="text-xs md:text-sm font-medium text-foreground">Example Realty Group</p>
                        </div>
                      </div>
                      <div className="relative aspect-square bg-muted">
                        <img 
                          src={demoPropertyImage} 
                          alt="Facebook post"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="p-2 md:p-3">
                        <p className="text-xs md:text-sm text-muted-foreground line-clamp-3">
                          NEW LISTING ALERT! 🎉 Check out this beautiful property in {DEMO_DATA.city}. Schedule your showing today!
                        </p>
                        <div className="flex items-center gap-2 md:gap-4 mt-2 md:mt-3 text-[10px] md:text-xs text-muted-foreground flex-wrap">
                          <span>1,924 reactions</span>
                          <span>203 comments</span>
                          <span className="hidden md:inline">35,671 engagement</span>
                        </div>
                      </div>
                    </div>

                    {/* YouTube Post */}
                    <div className="bg-background rounded-lg overflow-hidden border border-border shadow-sm">
                      <div className="p-2 md:p-3 flex items-center gap-2 border-b border-border">
                        <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-muted flex items-center justify-center">
                          <svg className="w-3 h-3 md:w-4 md:h-4" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                          </svg>
                        </div>
                        <div className="flex-1">
                          <p className="text-xs md:text-sm font-medium text-foreground">Homes Channel</p>
                        </div>
                      </div>
                      <div className="relative aspect-square bg-muted">
                        <img 
                          src={demoPropertyImage} 
                          alt="YouTube video"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                          <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-[#FF0000] flex items-center justify-center shadow-lg">
                            <svg className="w-6 h-6 md:w-8 md:h-8 text-white fill-white ml-1" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z"/>
                            </svg>
                          </div>
                        </div>
                      </div>
                      <div className="p-2 md:p-3">
                        <div className="mb-2">
                          <p className="text-xs md:text-sm font-medium text-foreground line-clamp-2">
                            {fullAddress} - Full Property Tour
                          </p>
                          <p className="text-[10px] md:text-xs text-muted-foreground mt-1">
                            12,483 views · Jan 15, 2025
                          </p>
                        </div>
                        <div className="flex items-center gap-2 md:gap-4 mt-2 md:mt-3 text-[10px] md:text-xs text-muted-foreground flex-wrap">
                          <span>847 likes</span>
                          <span>94 comments</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Showings */}
              <div className="mb-12 p-8 rounded-xl bg-gray-50 border border-gray-200 shadow-sm">
                <div>
                  <div>
                    <h3 className="text-sm font-semibold tracking-wider text-foreground mb-6">WHO CAME TO SEE YOUR HOME</h3>
                    
                    <div className="mb-6">
                      <div
                        className="text-foreground/90 leading-relaxed text-base"
                        dangerouslySetInnerHTML={{
                          __html: formatMarkdownToSafeHtml("Our strategic showing schedule generated **exceptional buyer interest** with **5 private showings** and **1 open house** in the first week alone, drawing 28 visitors to the weekend event. Through three weeks of marketing, we've conducted **11 total private showings** with highly qualified buyers. Feedback has been overwhelmingly positive, with multiple buyers expressing serious interest. Key highlights include **3 repeat showings** and several pre-qualified buyers ready to move forward.")
                        }}
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    {(showAllShowings ? DEMO_SHOWINGS : DEMO_SHOWINGS.slice(0, 5)).map((showing) => (
                      <div key={showing.id} className="bg-background border border-border rounded-lg p-6">
                        <div className="flex gap-4">
                          {/* Home Icon */}
                          <div className="flex-shrink-0">
                            <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                              <Home className="h-6 w-6 text-foreground" />
                            </div>
                          </div>

                          {/* Content */}
                          <div className="flex-1">
                            <div className="text-xs text-muted-foreground mb-1">
                              {format(new Date(showing.scheduled_date), 'M/d/yyyy')}
                            </div>
                            <h4 className="text-lg font-semibold text-foreground mb-2">
                              {showing.title}
                            </h4>
                            {showing.feedback && (
                              <p className="text-muted-foreground text-sm leading-relaxed">
                                {showing.feedback}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {DEMO_SHOWINGS.length > 5 && !showAllShowings && (
                      <div className="flex justify-center pt-4">
                        <Button 
                          variant="outline" 
                          onClick={() => setShowAllShowings(true)}
                          className="w-full sm:w-auto"
                        >
                          Load More Showings ({DEMO_SHOWINGS.length - 5} more)
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Floorplan & Tour */}
              {/* Check if we have floorplan or tour data in demo_reports or use placeholder */}
              <FloorplanTourPreview 
                northId={DEMO_NORTH_ID}
                isDemo={true}
                floorplanData={{
                  floorPlans: [
                    {
                      description: "Floor 1",
                      sqft: 1120.928,
                      url: "https://www.zillowstatic.com/floor_map/4faff124-df95-4e17-baec-ed6dd37edf4a/floor_shape/3efd27ff9e/original.png"
                    },
                    {
                      description: "Basement",
                      sqft: 454.5548,
                      url: "https://www.zillowstatic.com/floor_map/4faff124-df95-4e17-baec-ed6dd37edf4a/floor_shape/2eb0671fcf/original.png"
                    },
                    {
                      description: "Floor 2",
                      sqft: 1084.4796,
                      url: "https://www.zillowstatic.com/floor_map/4faff124-df95-4e17-baec-ed6dd37edf4a/floor_shape/4aa39de62f/original.png"
                    }
                  ]
                }}
                tourData={{
                  tourUrl: "https://www.zillow.com/view-3d-home/8627e489-a810-44bc-8124-257b07dd41cf",
                  viewerUrl: "https://www.zillow.com/view-3d-home/8627e489-a810-44bc-8124-257b07dd41cf",
                  provider: "Matterport",
                  embedUrl: null
                }}
                propertyImage={demoPropertyImage}
              />

              {/* Comps Section */}
              <div className="mb-12 p-8 rounded-xl bg-gray-50 border border-gray-200 shadow-sm">
                <div>
                  <h3 className="text-sm font-semibold tracking-wider text-foreground mb-6">HOW YOU COMPARE</h3>
                  
                  <div className="mb-6">
                    <div
                      className="text-foreground/90 leading-relaxed text-base"
                      dangerouslySetInnerHTML={{
                        __html: formatMarkdownToSafeHtml("Based on **comprehensive market analysis** of comparable properties, this home is competitively priced at **$1,250,000**. Recent nearby sales ranged from $1.1M to $1.4M, with similar square footage and amenities. The property's **modern upgrades** and **resort-style outdoor space** position it favorably within this range, offering excellent value for discerning buyers in today's market.")
                      }}
                    />
                  </div>
                </div>

                <div className="flex flex-col md:flex-row gap-6">
                  {/* Interactive Map - Show first on mobile */}
                  <div className="flex-shrink-0 h-[300px] md:h-auto md:w-[400px] order-last">
                    {!mapboxToken ? (
                      <div className="w-full h-full bg-muted rounded-lg overflow-hidden border border-border relative flex items-center justify-center">
                        <div className="text-center space-y-2 px-4">
                          <MapPin className="h-12 w-12 text-muted-foreground mx-auto" />
                          <p className="text-sm text-muted-foreground">
                            Loading map...
                          </p>
                        </div>
                      </div>
                    ) : webglError ? (
                      <div className="w-full h-full bg-muted rounded-lg overflow-hidden border border-border relative flex items-center justify-center">
                        <div className="text-center space-y-2 px-4">
                          <MapPin className="h-12 w-12 text-muted-foreground mx-auto" />
                          <p className="text-sm text-muted-foreground">
                            Map unavailable
                          </p>
                          <p className="text-xs text-muted-foreground/70">
                            Your browser doesn't support interactive maps
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div ref={mapContainer} className="w-full h-full rounded-lg overflow-hidden border border-border" />
                    )}
                  </div>

                  {/* Comparable Properties List - Single Column, Compact */}
                  <div className="flex-1 space-y-3">
                      {DEMO_COMPS.map((comp) => {
                      const formatPrice = (price: number | null) => {
                        if (!price) return '-';
                        return new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          maximumFractionDigits: 0,
                        }).format(price);
                      };

                      const formatStatus = (status: string | null) => {
                        if (!status) return 'Listed for sale';
                        return status
                          .split('_')
                          .map(word => word.charAt(0) + word.slice(1).toLowerCase())
                          .join(' ');
                      };

                      const getStatusBadgeColor = (status: string | null): string => {
                        if (!status) return '#374151';
                        const statusLower = status.toLowerCase();
                        if (statusLower.includes('sold') || statusLower === 'sold') {
                          return '#15803d';
                        }
                        if (statusLower.includes('sale') || statusLower.includes('active') || statusLower === 'for_sale') {
                          return '#1d4ed8';
                        }
                        if (statusLower.includes('pending')) {
                          return '#b45309';
                        }
                        return '#374151';
                      };

                      return (
                        <div key={comp.id} className="flex gap-3 p-2 border border-border rounded hover:shadow-sm transition-all bg-card">
                          {/* Property Image - Small Thumbnail */}
                          {comp.image_url ? (
                            <img
                              src={comp.image_url}
                              alt={comp.address}
                              className="w-20 h-16 object-cover rounded flex-shrink-0"
                              onError={(e) => {
                                e.currentTarget.src = '/placeholder.svg';
                              }}
                            />
                          ) : (
                            <div className="w-20 h-16 bg-muted rounded flex items-center justify-center flex-shrink-0">
                              <MapPin className="h-5 w-5 text-muted-foreground" />
                            </div>
                          )}

                          {/* Property Details - Very Compact */}
                          <div className="flex-1 min-w-0 flex flex-col justify-center gap-1">
                            <div>
                              <div className="font-semibold text-sm mb-0.5">{formatPrice(comp.price)}</div>

                              <h3 className="font-medium text-xs text-foreground leading-tight">
                                {comp.address}
                              </h3>

                              <p className="text-[10px] text-muted-foreground leading-tight">
                                {comp.city}, {comp.state} {comp.zip}
                              </p>
                            </div>
                          </div>

                          {/* Right side - Status and Button */}
                          <div className="flex-shrink-0 self-center flex flex-col items-end gap-1.5">
                            <div
                              className="inline-flex items-center rounded-full border border-transparent px-1.5 py-0 h-4 text-[10px] font-semibold"
                              style={{
                                backgroundColor: getStatusBadgeColor(comp.status),
                                color: 'white'
                              }}
                            >
                              {formatStatus(comp.status)}
                            </div>
                            
                            {comp.zillow_url && (
                              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-muted text-muted-foreground rounded text-[10px] font-medium cursor-not-allowed opacity-60">
                                <ZillowIcon className="h-3 w-3" />
                                <span>View Listing</span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Neighborhood Data Section */}
              <div className="mb-12 p-8 rounded-xl bg-gray-50 border border-gray-200 shadow-sm">
                <NeighborhoodDataPreview
                  neighborhoodData={{
                    metrics: {
                      activeListings: 45,
                      pendingListings: 38,
                      soldListings: 52,
                      newListings: 6,
                      priceReductions: 12,
                      priceReductionRate: 27,
                      activeDomMedian: 68,
                      soldDomMedian: 62
                    },
                    analysis: {
                      demandRating: 8,
                      buyerTrend: "Strong",
                      monthsOfInventory: 2.1
                    }
                  }}
                  neighborhoodSummary="The Beverly Hills 90210 market remains **highly competitive** with **38 pending sales** against **45 active listings**, achieving a strong demand rating of **8 out of 10**. This luxury market maintains **2.1 months of inventory**, indicating sustained seller advantage. Buyer competition is currently **strong** with qualified buyers actively seeking premium properties. Homes typically spend **68 days on market** for active listings and **62 days for sold listings**, reflecting the selective nature of luxury buyers. Notably, only **27% of active listings have price reductions**, demonstrating strong price discipline in this high-end market. Properties priced correctly for the Beverly Hills market are commanding premium values and attracting serious, well-qualified buyers."
                  forceMobileLayout={false}
                />
              </div>

              {/* Agent Insights Section */}
              <AgentInsightsSection 
                northId={DEMO_NORTH_ID}
                primaryColor="#0c0f24"
                forceMobileLayout={false}
              />

              {/* Footer */}
              <div className="flex items-center justify-between pt-8 mt-8 border-t border-border">
                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground">Powered with</span>
                  <img src={defaultLogoUrl} alt="North Logo" className="h-8" />
                </div>
                <div className="text-xs text-muted-foreground">
                  Published: {new Date().toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric', 
                    year: 'numeric' 
                  })}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
